import type { DeductionListJson } from "@/api"

/** Parent deduction IDs referenced by repayment rows still present in the list. */
export function repayment_parent_ids_from_list(data: DeductionListJson[]): Set<string> {
  return new Set(data.filter(row => row.kind === "repayment" && row.repayment_for).map(row => row.repayment_for!))
}

/** Won invoice/deduction rows that still need payment (no linked repayment in list and none subsumed server-side). */
export function filter_won_pending_payment_rows(data: DeductionListJson[]): DeductionListJson[] {
  const repaymentParentIds = repayment_parent_ids_from_list(data)
  return data.filter(
    row =>
      row.status_value === "won" &&
      row.kind !== "repayment" &&
      !repaymentParentIds.has(row.id) &&
      !(row.has_matched_repayment ?? false),
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function base_row(overrides: Partial<DeductionListJson> & Pick<DeductionListJson, "id" | "kind" | "status_value">): DeductionListJson {
    return {
      org_id: "o1",
      created_at: "",
      updated_at: "",
      source: "unfi",
      invoice_amount: 0,
      discount_amount: 0,
      gross_amount: 0,
      invoice_date: "2024-01-01",
      invoice_number: "",
      check_amount: 0,
      check_date: "",
      check_number: "",
      category_id: "",
      backup_s3_uri: null,
      check_s3_uri: null,
      user_files: [],
      dispute_id: "",
      retailer_name: null,
      execution_date: "",
      document: "",
      data: "",
      product_line: "",
      retailer_invoice_number: "",
      has_splits: false,
      is_parent: false,
      import_source: "portal",
      pending: false,
      category: "",
      category_description: "",
      customer_name: "",
      validated: false,
      deduction_amount: 100,
      has_notes: false,
      has_backup: false,
      ...overrides,
    } as DeductionListJson
  }

  describe("filter_won_pending_payment_rows", () => {
    it("excludes won parent when has_matched_repayment is true (subsumed repayment)", () => {
      const parentId = "parent-1"
      const rows = [
        base_row({
          id: parentId,
          kind: "deduction",
          status_value: "won",
          has_matched_repayment: true,
        }),
      ]
      expect(filter_won_pending_payment_rows(rows)).toHaveLength(0)
    })

    it("excludes won parent when a repayment row points at it", () => {
      const parentId = "parent-2"
      const rows = [
        base_row({ id: parentId, kind: "deduction", status_value: "won" }),
        base_row({
          id: "repay-1",
          kind: "repayment",
          status_value: "won",
          repayment_for: parentId,
        }),
      ]
      expect(filter_won_pending_payment_rows(rows).map(r => r.id)).toEqual([])
    })

    it("includes won parent with no repayment link and no subsumed flag", () => {
      const rows = [base_row({ id: "p3", kind: "deduction", status_value: "won" })]
      expect(filter_won_pending_payment_rows(rows).map(r => r.id)).toEqual(["p3"])
    })
  })
}
